import { useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationsIcon from "@mui/icons-material/Notifications";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { LAYOUT } from "../../pages/appointment/makeappointment/components/appointmentScheduleUi";
import type { SmsAlertData } from "../../hooks/useSmsAlert";
import type { ChannelAlert } from "../../hooks/useChannelAlerts";

type SmsNotificationButtonProps = {
  alert?: SmsAlertData | null;
  /** WhatsApp / email token alerts, shown under the SMS one. */
  channelAlerts?: ChannelAlert[];
};

export default function SmsNotificationButton({
  alert = null,
  channelAlerts = [],
}: SmsNotificationButtonProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const showSms = Boolean(alert?.enabled && alert?.showAlert);
  const showBadge = showSms || channelAlerts.length > 0;
  const isCritical =
    alert?.level === "depleted" || channelAlerts.some((item) => item.level === "depleted");
  const badgeClass = isCritical ? "bg-red-500" : "bg-amber-500";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${LAYOUT.iconBtn} relative`}
        aria-label={
          showBadge
            ? isCritical
              ? "Message tokens finished"
              : "Message balance low"
            : "Notifications"
        }
        title={
          showSms && alert?.message
            ? alert.message
            : channelAlerts[0]?.message || "Notifications"
        }
      >
        <NotificationsIcon sx={{ fontSize: 22 }} />
        {showBadge && (
          <span
            className={`absolute top-1 right-1 h-2 w-2 rounded-full ${badgeClass}`}
          />
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-[90]"
            onClick={() => setOpen(false)}
            aria-hidden
          />
          <div
            className={`${LAYOUT.menuPanel} absolute right-0 top-full mt-2 z-[110] w-72`}
            role="dialog"
            aria-label="SMS notifications"
          >
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
              <p className="text-sm font-semibold text-slate-800 dark:text-white">
                Notifications
              </p>
            </div>

            {showSms && alert?.message ? (
              <div className="px-4 py-3">
                <div className="flex items-start gap-2.5">
                  {alert.level === "depleted" ? (
                    <ErrorOutlineIcon
                      className="mt-0.5 shrink-0 text-red-500"
                      sx={{ fontSize: 20 }}
                    />
                  ) : (
                    <WarningAmberOutlinedIcon
                      className="mt-0.5 shrink-0 text-amber-500"
                      sx={{ fontSize: 20 }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {alert.level === "depleted" ? "SMS tokens finished" : "SMS balance low"}
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      {alert.message}
                    </p>
                    {alert.tokenCredits != null && (
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        {alert.tokenCredits} token
                        {alert.tokenCredits === 1 ? "" : "s"} remaining
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate("/client/sms-topup");
                      }}
                      className="mt-2 text-sm font-medium text-primary underline underline-offset-2 hover:opacity-80"
                    >
                      Buy SMS tokens
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {channelAlerts.map((item) => (
              <div
                key={item.channel}
                className="border-t border-slate-100 px-4 py-3 first:border-t-0 dark:border-slate-700"
              >
                <div className="flex items-start gap-2.5">
                  {item.level === "depleted" ? (
                    <ErrorOutlineIcon className="mt-0.5 shrink-0 text-red-500" sx={{ fontSize: 20 }} />
                  ) : (
                    <WarningAmberOutlinedIcon
                      className="mt-0.5 shrink-0 text-amber-500"
                      sx={{ fontSize: 20 }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 dark:text-white">
                      {item.level === "depleted"
                        ? `${item.label} tokens finished`
                        : `${item.label} balance low`}
                    </p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{item.message}</p>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {item.tokenCredits} token{item.tokenCredits === 1 ? "" : "s"} remaining
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        navigate(item.topUpPath);
                      }}
                      className="mt-2 text-sm font-medium text-primary underline underline-offset-2 hover:opacity-80"
                    >
                      Buy {item.label} tokens
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {!showBadge ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                No new notifications
              </div>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
